import React from "react"
import { View } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import { notifyBugsnag } from "@common/utils/initBugsnag"
import { CAUTION } from "./precallColors"

/**
 * Le test matériel ne doit jamais empêcher d'entrer en séance.
 *
 * Le panneau ouvre des flux média, tient des minuteries et lit des API
 * navigateur dont le comportement varie d'une machine à l'autre. Or le seul
 * filet de l'application est posé tout en haut, autour d'`App` : sans celui-ci,
 * une exception dans le test n'aurait pas dégradé le test — elle aurait envoyé
 * toute l'application sur son écran d'erreur, et l'élève n'aurait plus eu aucun
 * moyen de rejoindre son cours. Un écran de vérification qui empêche d'entrer
 * en cours est pire que pas de vérification du tout.
 *
 * Ce qui subsiste quand le test tombe : le bouton « Rejoindre maintenant », qui
 * vit en dehors de ce périmètre, et une phrase qui dit à l'élève d'y aller.
 *
 * Encore faut-il que ce bouton soit un bouton. L'écran le garde discret tant
 * que le test n'est pas fini — un lien gris, pour ne pas concurrencer l'étape
 * en cours. Or un test qui vient de tomber ne finira jamais : sans `onFailed`,
 * la seule action possible restait affichée comme une note de bas de page.
 */
class PreCallCheckBoundary extends React.Component {
    constructor(props) {
        super(props)
        this.state = { failed: false }
    }

    static getDerivedStateFromError() {
        return { failed: true }
    }

    componentDidCatch(error) {
        // Silencieux pour l'élève, visible pour nous. Sans ce signalement, un
        // test qui tombe ne bloque plus rien — et ne se remarque plus non plus.
        notifyBugsnag(error)
        // Le test est terminé, faute d'avoir pu se faire : à l'écran de rendre
        // l'entrée pleinement visible.
        this.props.onFailed?.()
    }

    render() {
        if (!this.state.failed) return this.props.children

        return (
            <View
                style={[
                    {
                        flexDirection: "row",
                        alignItems: "center",
                        backgroundColor: theme.colors.warningLight,
                        borderRadius: 18,
                        borderWidth: 1,
                        borderColor: theme.colors.warningLighter,
                        paddingVertical: 15,
                        paddingHorizontal: 16,
                    },
                    this.props.style,
                ]}
            >
                <FontAwesome5 name="tools" size={17} color={CAUTION} />
                <TextM color={CAUTION} fontSize={13} style={{ flex: 1, marginLeft: 12 }} wrap>
                    Je n'ai pas réussi à faire le test. Ce n'est pas grave : entre dans ton live, et écris-le dans le chat si on ne te voit pas ou ne t'entend pas.
                </TextM>
            </View>
        )
    }
}

export default PreCallCheckBoundary
